import { makeAutoObservable, runInAction } from 'mobx';
import type {
  CompanyReviewAnalysisSummary,
  FeatureViewUsageSummary,
  LoginStatistics,
  RagDocument,
  TokenUsageSummary
} from '../entity';
import { applyBaseStore } from './BaseStore';
import type { BaseStore } from './BaseStore';

export interface AdminStore extends BaseStore {}

const scopeAccessEmails = (response: unknown): string[] => {
  if (
    !response ||
    typeof response !== 'object' ||
    !Array.isArray(response['emails'])
  ) {
    return [];
  }
  return response['emails'].map((email) => String(email));
};

const createdScopeAccessEmails = (response: unknown): string[] => {
  if (
    !response ||
    typeof response !== 'object' ||
    !Array.isArray(response['access_emails'])
  ) {
    return [];
  }
  return response['access_emails'].map((email) => String(email));
};

export class AdminStore {
  chatbotRagProjects: string[] = [];

  chatbotRagDocuments: RagDocument[] = [];

  chatbotRagScopeAccess: string[] = [];

  chatbotRagScopeAccessError = false;

  chatbotRagLoading = false;

  chatbotRagUploadActive = false;

  chatbotRagUploadTotal = 0;

  chatbotRagUploadCompleted = 0;

  chatbotRagUploadCurrentFile = '';

  chatbotRagUploadFailures: string[] = [];

  tokenUsage?: TokenUsageSummary;

  tokenUsageLoading = false;

  featureViewUsage?: FeatureViewUsageSummary;

  featureViewUsageLoading = false;

  loginStatistics?: LoginStatistics;

  loginStatisticsLoading = false;

  companyReviewAnalysis?: CompanyReviewAnalysisSummary;

  companyReviewAnalysisLoading = false;

  constructor() {
    applyBaseStore(this);
    makeAutoObservable(this);
  }

  async startCompanyReviewAnalysis(company?: string) {
    runInAction(() => {
      this.companyReviewAnalysisLoading = true;
    });

    try {
      const response = await this.api.fetch({
        endpoint: `${this.endpointAdminRag}/company-review`,
        method: 'POST',
        body: JSON.stringify(company ? { company } : {})
      });
      await this.fetchCompanyReviewAnalysis();
      return response;
    } finally {
      runInAction(() => {
        this.companyReviewAnalysisLoading = false;
      });
    }
  }

  async fetchProjects() {
    const response = await this.api.fetch({
      endpoint: `${this.endpointAdminRag}/chatbot/projects`,
      method: 'GET'
    });

    if (
      !response ||
      typeof response !== 'object' ||
      !Array.isArray(response['projects'])
    ) {
      runInAction(() => {
        this.chatbotRagProjects = ['x1F'];
      });
      return;
    }

    runInAction(() => {
      this.chatbotRagProjects = response['projects'].map((project) =>
        String(project)
      );
    });
  }

  async fetchDocuments(project: string) {
    runInAction(() => {
      this.chatbotRagLoading = true;
    });

    try {
      const response = await this.api.fetch({
        endpoint: `${this.endpointAdminRag}/chatbot/documents`,
        method: 'GET',
        queryParams: { project, limit: 10000 }
      });

      const documents =
        response &&
        typeof response === 'object' &&
        Array.isArray(response['documents'])
          ? (response['documents'] as RagDocument[])
          : [];

      runInAction(() => {
        this.chatbotRagDocuments = documents;
      });
    } finally {
      runInAction(() => {
        this.chatbotRagLoading = false;
      });
    }
  }

  async fetchScopeAccess(project: string) {
    runInAction(() => {
      this.chatbotRagScopeAccessError = false;
    });

    try {
      const response = await this.api.fetch({
        endpoint: `${
          this.endpointAdminRag
        }/chatbot/projects/${encodeURIComponent(project)}/access`,
        method: 'GET'
      });

      runInAction(() => {
        this.chatbotRagScopeAccess = scopeAccessEmails(response);
      });
    } catch {
      runInAction(() => {
        this.chatbotRagScopeAccess = [];
        this.chatbotRagScopeAccessError = true;
      });
    }
  }

  async fetchTokenUsage() {
    runInAction(() => {
      this.tokenUsageLoading = true;
    });

    try {
      const response = await this.api.fetch({
        endpoint: `${this.endpointAdminRag}/token-usage`,
        method: 'GET'
      });

      runInAction(() => {
        this.tokenUsage =
          response && typeof response === 'object'
            ? (response as TokenUsageSummary)
            : undefined;
      });
    } catch {
      runInAction(() => {
        this.tokenUsage = undefined;
      });
    } finally {
      runInAction(() => {
        this.tokenUsageLoading = false;
      });
    }
  }

  async fetchFeatureViewUsage() {
    runInAction(() => {
      this.featureViewUsageLoading = true;
    });

    try {
      const response = await this.api.fetch({
        endpoint: `${this.endpointAdminRag}/view-usage`,
        method: 'GET'
      });

      runInAction(() => {
        this.featureViewUsage =
          response && typeof response === 'object'
            ? (response as FeatureViewUsageSummary)
            : undefined;
      });
    } catch {
      runInAction(() => {
        this.featureViewUsage = undefined;
      });
    } finally {
      runInAction(() => {
        this.featureViewUsageLoading = false;
      });
    }
  }

  async fetchLoginStatistics() {
    runInAction(() => {
      this.loginStatisticsLoading = true;
    });

    try {
      const response = await this.api.fetch({
        endpoint: `${this.endpointAdminRag}/login-statistics`,
        method: 'GET'
      });

      runInAction(() => {
        this.loginStatistics =
          response && typeof response === 'object'
            ? (response as LoginStatistics)
            : undefined;
      });
    } catch {
      runInAction(() => {
        this.loginStatistics = undefined;
      });
    } finally {
      runInAction(() => {
        this.loginStatisticsLoading = false;
      });
    }
  }

  async fetchCompanyReviewAnalysis() {
    runInAction(() => {
      this.companyReviewAnalysisLoading = true;
    });

    try {
      const response = await this.api.fetch({
        endpoint: `${this.endpointAdminRag}/company-review`,
        method: 'GET'
      });

      runInAction(() => {
        this.companyReviewAnalysis =
          response && typeof response === 'object'
            ? (response as CompanyReviewAnalysisSummary)
            : undefined;
      });
    } catch {
      runInAction(() => {
        this.companyReviewAnalysis = undefined;
      });
    } finally {
      runInAction(() => {
        this.companyReviewAnalysisLoading = false;
      });
    }
  }

  async uploadDocuments(project: string, files: File[]) {
    if (!files.length) {
      return;
    }

    runInAction(() => {
      this.chatbotRagLoading = true;
      this.chatbotRagUploadActive = true;
      this.chatbotRagUploadTotal = files.length;
      this.chatbotRagUploadCompleted = 0;
      this.chatbotRagUploadCurrentFile = '';
      this.chatbotRagUploadFailures = [];
    });

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('files', file);

        runInAction(() => {
          this.chatbotRagUploadCurrentFile = file.name;
        });

        try {
          const response = await this.api.fetch({
            endpoint: `${
              this.endpointAdminRag
            }/chatbot/documents/${encodeURIComponent(project)}`,
            method: 'POST',
            body: formData,
            contentType: 'auto'
          });

          if (
            response &&
            typeof response === 'object' &&
            Array.isArray(response['projects'])
          ) {
            const projects = response['projects'].map((value) => String(value));
            runInAction(() => {
              this.chatbotRagProjects = projects;
            });
          }
        } catch {
          runInAction(() => {
            this.chatbotRagUploadFailures = [
              ...this.chatbotRagUploadFailures,
              file.name
            ];
          });
        } finally {
          runInAction(() => {
            this.chatbotRagUploadCompleted += 1;
          });
        }
      }

      await this.fetchDocuments(project);
    } finally {
      runInAction(() => {
        this.chatbotRagLoading = false;
        this.chatbotRagUploadActive = false;
        this.chatbotRagUploadCurrentFile = '';
      });
    }
  }

  async createProject(project: string, accessEmails: string[] = []) {
    runInAction(() => {
      this.chatbotRagLoading = true;
    });

    try {
      const response = await this.api.fetch({
        endpoint: `${this.endpointAdminRag}/chatbot/projects`,
        method: 'POST',
        body: JSON.stringify({
          project_title: project,
          access_emails: accessEmails
        })
      });
      const projects =
        response &&
        typeof response === 'object' &&
        Array.isArray(response['projects'])
          ? response['projects'].map((value) => String(value))
          : await this.fetchProjects();

      if (projects) {
        runInAction(() => {
          this.chatbotRagProjects = projects;
          this.chatbotRagScopeAccess = createdScopeAccessEmails(response);
          this.chatbotRagScopeAccessError = false;
        });
      }
    } finally {
      runInAction(() => {
        this.chatbotRagLoading = false;
      });
    }
  }

  async addScopeAccess(project: string, email: string) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointAdminRag}/chatbot/projects/${encodeURIComponent(
        project
      )}/access`,
      method: 'POST',
      body: JSON.stringify({ email })
    });
    runInAction(() => {
      this.chatbotRagScopeAccess = scopeAccessEmails(response);
      this.chatbotRagScopeAccessError = false;
    });
  }

  async removeScopeAccess(project: string, email: string) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointAdminRag}/chatbot/projects/${encodeURIComponent(
        project
      )}/access`,
      method: 'DELETE',
      queryParams: { email }
    });
    runInAction(() => {
      const emails = scopeAccessEmails(response);
      this.chatbotRagScopeAccess = emails.length
        ? emails
        : this.chatbotRagScopeAccess.filter((value) => value !== email);
      this.chatbotRagScopeAccessError = false;
    });
  }

  async deleteDocument(project: string, documentId: number) {
    runInAction(() => {
      this.chatbotRagLoading = true;
    });

    try {
      await this.api.fetch({
        endpoint: `${this.endpointAdminRag}/chatbot/documents/${documentId}`,
        method: 'DELETE',
        queryParams: { project }
      });
      await this.fetchDocuments(project);
    } finally {
      runInAction(() => {
        this.chatbotRagLoading = false;
      });
    }
  }

  async deleteProject(project: string) {
    runInAction(() => {
      this.chatbotRagLoading = true;
    });

    try {
      const response = await this.api.fetch({
        endpoint: `${
          this.endpointAdminRag
        }/chatbot/projects/${encodeURIComponent(project)}`,
        method: 'DELETE'
      });

      const projects =
        response &&
        typeof response === 'object' &&
        Array.isArray(response['projects'])
          ? response['projects'].map((value) => String(value))
          : this.chatbotRagProjects.filter((value) => value !== project);

      runInAction(() => {
        this.chatbotRagProjects = projects.length ? projects : ['x1F'];
        this.chatbotRagDocuments = [];
        this.chatbotRagScopeAccess = [];
        this.chatbotRagScopeAccessError = false;
      });
    } finally {
      runInAction(() => {
        this.chatbotRagLoading = false;
      });
    }
  }
}
